if (!customElements.get('subscribe-popup')) {
  const SUBSCRIBE_POPUP_STORAGE_KEY = 'cosmogen:subscribe-popup';
  const DAY_MS = 24 * 60 * 60 * 1000;

  customElements.define(
    'subscribe-popup',
    class SubscribePopup extends HTMLElement {
      constructor() {
        super();
        this.onSectionEvent = this.onSectionEvent.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
      }

      connectedCallback() {
        this.dialog = this.querySelector('.subscribe-popup__dialog');

        this.querySelectorAll('[data-popup-close]').forEach((element) =>
          element.addEventListener('click', () => this.close())
        );
        this.querySelector('[data-popup-copy]')?.addEventListener('click', (event) =>
          this.copyCode(event.currentTarget)
        );

        if (window.Shopify?.designMode) {
          ['shopify:section:load', 'shopify:section:select', 'shopify:section:deselect'].forEach((name) =>
            document.addEventListener(name, this.onSectionEvent)
          );
          return;
        }

        // Shopify redirects back to #SubscribePopupForm after submit: show the code or the error.
        if (window.location.hash === `#${this.dataset.formId}`) {
          if (this.querySelector('[data-popup-success]')) this.writeState({ subscribed: true });
          this.open();
          return;
        }

        if (this.isSuppressed()) return;
        this.openTimer = setTimeout(() => this.open(), Number(this.dataset.delay) * 1000);
      }

      disconnectedCallback() {
        clearTimeout(this.openTimer);
        ['shopify:section:load', 'shopify:section:select', 'shopify:section:deselect'].forEach((name) =>
          document.removeEventListener(name, this.onSectionEvent)
        );
        document.removeEventListener('keydown', this.onKeyDown);
        document.body.classList.remove('overflow-hidden');
      }

      // Listens on document so Escape works even when focus has left the dialog.
      onKeyDown(event) {
        if (event.code === 'Escape') this.close();
      }

      onSectionEvent(event) {
        if (event.detail.sectionId !== this.dataset.sectionId) return;
        if (event.type === 'shopify:section:deselect') {
          this.close(false);
        } else {
          this.open();
        }
      }

      open() {
        if (this.hasAttribute('open')) return;
        this.previouslyFocused = document.activeElement;
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onKeyDown);
        if (!window.Shopify?.designMode) {
          window.cosmogenAnalytics?.track('popup_view', {
            popup_name: 'subscribe_popup',
            popup_state: this.querySelector('[data-popup-success]') ? 'success' : 'form',
          });
        }
        const errorInput = this.querySelector('[aria-invalid="true"]');
        trapFocus(this.dialog, errorInput || this.dialog);
      }

      close(rememberDismissal = true) {
        if (!this.hasAttribute('open')) return;
        this.removeAttribute('open');
        document.removeEventListener('keydown', this.onKeyDown);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.previouslyFocused);
        if (rememberDismissal) {
          window.cosmogenAnalytics?.track('popup_close', {
            popup_name: 'subscribe_popup',
            popup_state: this.querySelector('[data-popup-success]') ? 'success' : 'form',
          });
        }
        if (rememberDismissal && !this.readState()?.subscribed) {
          this.writeState({ dismissedAt: Date.now() });
        }
      }

      isSuppressed() {
        const state = this.readState();
        if (!state) return false;
        if (state.subscribed) return true;
        return Date.now() - state.dismissedAt < Number(this.dataset.hideDays) * DAY_MS;
      }

      readState() {
        try {
          return JSON.parse(localStorage.getItem(SUBSCRIBE_POPUP_STORAGE_KEY));
        } catch (error) {
          return null;
        }
      }

      writeState(state) {
        try {
          localStorage.setItem(SUBSCRIBE_POPUP_STORAGE_KEY, JSON.stringify(state));
        } catch (error) {
          // Storage can be blocked (private mode); the popup then simply shows again next visit.
        }
      }

      async copyCode(button) {
        try {
          await navigator.clipboard.writeText(button.dataset.popupCopy);
          button.textContent = button.dataset.copiedLabel;
          window.cosmogenAnalytics?.track('promo_code_copy', { coupon: button.dataset.popupCopy, location: 'subscribe_popup' });
        } catch (error) {
          // Clipboard unavailable: the code stays visible and selectable.
        }
      }
    }
  );
}
